import {
  Button,
  Label,
  RadioGroup, RadioGroupItem,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Checkbox,
} from '@creanizer/ui'
import { Upload, X, Trash2 } from 'lucide-react'
import type { ImageAspectRatio, ImageSize, ImageGenerateMode } from '@creanizer/types'
import { MaskCanvas } from './MaskCanvas'

interface ImageGenerateSettingsProps {
  mode: ImageGenerateMode
  onModeChange: (mode: ImageGenerateMode) => void
  referencePreviews: string[]
  onReferenceUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
  onReferenceRemove: (index: number) => void
  onReferenceClearAll: () => void
  maxImages: number
  aspectRatio: ImageAspectRatio
  onAspectRatioChange: (ratio: ImageAspectRatio) => void
  imageSize: ImageSize
  onImageSizeChange: (size: ImageSize) => void
  // マスク関連
  maskEnabled: boolean
  onMaskEnabledChange: (enabled: boolean) => void
  onMaskChange: (maskDataUrl: string | null) => void
  // 挿入画像関連
  insertImage: string | null
  onInsertImageChange: (image: string | null) => void
}

export function ImageGenerateSettings({
  mode,
  onModeChange,
  referencePreviews,
  onReferenceUpload,
  onReferenceRemove,
  onReferenceClearAll,
  maxImages,
  aspectRatio,
  onAspectRatioChange,
  imageSize,
  onImageSizeChange,
  maskEnabled,
  onMaskEnabledChange,
  onMaskChange,
  insertImage,
  onInsertImageChange,
}: ImageGenerateSettingsProps) {
  const remainingSlots = maxImages - referencePreviews.length
  const canUploadMore = remainingSlots > 0
  const canUseMask = referencePreviews.length === 1

  return (
    <>
      {/* 生成モード */}
      <div className="space-y-3">
        <Label>生成モード</Label>
        <RadioGroup value={mode} onValueChange={(v) => onModeChange(v as ImageGenerateMode)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="text_to_image" id="mode-text" />
            <Label htmlFor="mode-text">テキストから生成</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="image_edit" id="mode-edit" />
            <Label htmlFor="mode-edit">画像改変（参考画像 + プロンプト）</Label>
          </div>
        </RadioGroup>
      </div>

      {/* 参考画像アップロード（画像改変モード時） */}
      {mode === 'image_edit' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>参考画像（最大{maxImages}枚）</Label>
            {referencePreviews.length > 0 && (
              <Button size="sm" variant="ghost" onClick={onReferenceClearAll} className="text-red-500">
                <Trash2 className="h-3 w-3 mr-1" />
                全削除
              </Button>
            )}
          </div>

          <input
            id="reference-image-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={onReferenceUpload}
            className="hidden"
          />

          {/* 画像プレビュー一覧 */}
          {referencePreviews.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {referencePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Reference ${index + 1}`}
                    className="w-full h-20 object-cover rounded border"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute top-1 right-1 h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
                    onClick={() => onReferenceRemove(index)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                  <span className="absolute bottom-1 left-1 text-xs bg-black/60 text-white px-1 rounded">
                    {index + 1}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* アップロードボタン */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => document.getElementById('reference-image-upload')?.click()}
              disabled={!canUploadMore}
            >
              <Upload className="h-4 w-4 mr-2" />
              画像を追加
            </Button>
            <span className="text-sm text-gray-500">
              {referencePreviews.length}/{maxImages}枚
              {canUploadMore && ` (残り${remainingSlots}枚)`}
            </span>
          </div>

          {referencePreviews.length > 1 && (
            <p className="text-xs text-blue-600">
              複数画像を組み合わせて新しい画像を生成できます
            </p>
          )}

          {/* マスク編集（参照画像が1枚の場合のみ） */}
          {canUseMask && (
            <div className="space-y-3 border-t pt-3 mt-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="mask-enabled"
                  checked={maskEnabled}
                  onCheckedChange={(checked) => {
                    onMaskEnabledChange(checked === true)
                    if (!checked) onMaskChange(null)
                  }}
                />
                <Label htmlFor="mask-enabled" className="cursor-pointer">
                  マスク編集（インペイント風）を有効にする
                </Label>
              </div>

              {maskEnabled && (
                <>
                  <MaskCanvas
                    referenceImage={referencePreviews[0]}
                    onMaskChange={onMaskChange}
                  />

                  {/* 挿入画像（マスク部分に合成する画像） */}
                  <div className="space-y-2 border-t pt-3 mt-3">
                    <Label className="text-sm">挿入画像（オプション）</Label>
                    <p className="text-xs text-gray-500">
                      マスク部分に合成したい画像をアップロードできます
                    </p>

                    <input
                      id="insert-image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          const reader = new FileReader()
                          reader.onload = () => {
                            onInsertImageChange(reader.result as string)
                          }
                          reader.readAsDataURL(file)
                        }
                        e.target.value = ''
                      }}
                    />

                    {insertImage ? (
                      <div className="flex items-center gap-3">
                        <div className="relative group">
                          <img
                            src={insertImage}
                            alt="Insert"
                            className="w-20 h-20 object-cover rounded border"
                          />
                          <Button
                            size="sm"
                            variant="destructive"
                            className="absolute top-1 right-1 h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
                            onClick={() => onInsertImageChange(null)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <span className="text-xs text-green-600">
                          この画像がマスク部分に合成されます
                        </span>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('insert-image-upload')?.click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        挿入画像を選択
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {referencePreviews.length > 1 && maskEnabled && (
            <p className="text-xs text-amber-600">
              マスク編集は参照画像が1枚の場合のみ使用できます
            </p>
          )}
        </div>
      )}

      {/* 画像設定 */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>アスペクト比</Label>
          <Select value={aspectRatio} onValueChange={(v) => onAspectRatioChange(v as ImageAspectRatio)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="9:16">9:16（縦長）</SelectItem>
              <SelectItem value="16:9">16:9（横長）</SelectItem>
              <SelectItem value="1:1">1:1（正方形）</SelectItem>
              <SelectItem value="4:3">4:3</SelectItem>
              <SelectItem value="3:4">3:4</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>画像サイズ</Label>
          <RadioGroup
            value={imageSize}
            onValueChange={(v) => onImageSizeChange(v as ImageSize)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-1">
              <RadioGroupItem value="1K" id="size-1k" />
              <Label htmlFor="size-1k">1K</Label>
            </div>
            <div className="flex items-center space-x-1">
              <RadioGroupItem value="2K" id="size-2k" />
              <Label htmlFor="size-2k">2K</Label>
            </div>
            <div className="flex items-center space-x-1">
              <RadioGroupItem value="4K" id="size-4k" />
              <Label htmlFor="size-4k">4K</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
    </>
  )
}
